import React, { useEffect, useState } from 'react';
import {
  Button,
  Container,
  Copy,
  Flex,
  Heading,
  Link,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { SignupStepFooter } from 'src/components/common/features/Signup/components/SignupStepFooter/SignupStepFooter';
import { useEnterpriseSignupStatus } from 'src/components/EnterpriseSignupStatusProvider/EnterpriseSignupStatusProvider';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import { EnterpriseLocationTable } from 'src/components/EnterpriseSignupSteps/EnterpriseCompanyDetailsStep/EnterpriseLocationTable';
import { EnterpriseDepartmentTable } from 'src/components/EnterpriseSignupSteps/EnterpriseCompanyDetailsStep/EnterpriseDepartmentTable';
import { EnterpriseInvoiceRecipientTable } from 'src/components/enterprise/set-up/steps/EnterpriseInvoiceSettingsStep/EnterpriseInvoiceRecipientTable';
import { EnterpriseBackToHomeButton } from 'src/components/EnterpriseBackToHomeButton/EnterpriseBackToHomeButton';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

export function EnterpriseInvoiceSettingsStep(): JSX.Element {
  const { goToPreviousStep, goToNextStep, corpId } =
    useEnterpriseSignupStatus();

  const { corpData } = useAuth();
  const { sendTealiumPageView } = useAnalytics();

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin - signup flow',
      pagesubgroup: 'invoice settings',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [corpHasDepartments, setCorpHasDepartments] = useState(false);
  const [corpHasLocations, setCorpHasLocations] = useState(false);

  const areLocationsRequired = corpData.locationsRequired;
  const areDepartmentsRequired = corpData.departmentsRequired;
  const showLocationsAndDepartments =
    !areLocationsRequired && !areDepartmentsRequired;

  const shouldDisableSubmitButton =
    (areLocationsRequired && !corpHasLocations) ||
    (areDepartmentsRequired && !corpHasDepartments);

  return (
    <>
      <Container variant="md">
        <Stack y="md" marginTop={12}>
          <Flex justifyContent="space-between">
            <Heading variant="h3">Set up your invoices</Heading>
            <EnterpriseBackToHomeButton />
          </Flex>
        </Stack>
        {(showLocationsAndDepartments || areLocationsRequired) && (
          <Stack y="md">
            <Stack y="x-sm">
              <Heading variant="h6">
                Your locations {!areLocationsRequired && '(optional)'}
              </Heading>
            </Stack>
            <Stack y="sm">
              <Copy variant="body">
                Manage invoices by locations, like an office or building.
              </Copy>
            </Stack>
            <EnterpriseLocationTable
              hasItemsCb={(hasLocations) => setCorpHasLocations(hasLocations)}
            />
          </Stack>
        )}
        {(showLocationsAndDepartments || areDepartmentsRequired) && (
          <Stack y="md">
            <Stack y="x-sm">
              <Heading variant="h6">
                Your departments {!areDepartmentsRequired && '(optional)'}
              </Heading>
            </Stack>
            <Stack y="sm">
              <Copy variant="body">
                Manage invoices by departments, like Finance or HR.
              </Copy>
            </Stack>
            <EnterpriseDepartmentTable
              hasItemsCb={(hasDepartments) =>
                setCorpHasDepartments(hasDepartments)
              }
            />
          </Stack>
        )}
        <Stack y="md">
          <Stack y="x-sm">
            <Heading variant="h6">Invoice recipients (optional)</Heading>
          </Stack>
          <Stack y="sm" data-testid="invoice-recipient-section-description">
            <Copy variant="body">
              Share invoice summaries with others via email.{' '}
              <Link
                href="https://media-cdn.grubhub.com/image/upload/v1641399505/corporate/Invoice-example.pdf"
                target="_blank"
                rel="noreferrer noopener"
              >
                See an example
              </Link>{' '}
              of what recipients will receive.
            </Copy>
          </Stack>
          <EnterpriseInvoiceRecipientTable corpId={corpId} />
        </Stack>
      </Container>
      <SignupStepFooter containerSize="md">
        <Flex justifyContent="space-between">
          <Button
            variant="tertiary"
            buttonSize="medium"
            onClick={goToPreviousStep}
          >
            Back
          </Button>
          <Button
            variant="primary"
            buttonSize="medium"
            onClick={goToNextStep}
            disabled={shouldDisableSubmitButton}
          >
            Save and continue
          </Button>
        </Flex>
      </SignupStepFooter>
    </>
  );
}
